import type { SchemaToType } from '@main/schema-utils';
import { mkInput, mkType } from '@main/schema-utils';

export const SensitiveCategory = mkType({
  name: 'SensitiveCategory',
  comment: 'A sensitive category of the organization',
  fields: {
    id: {
      comment: 'The unique ID of the sensitive category',
      modelName: 'sensitiveCategory',
      type: 'id',
    },
    title: {
      comment: 'The title of the sensitive category',
      type: 'string',
    },
    color: {
      comment: 'The color of the sensitive category',
      type: 'string',
    },
    description: {
      comment: 'The description of the sensitive category',
      type: 'string',
    },
  },
});

/** Override type */
export type SensitiveCategory = SchemaToType<typeof SensitiveCategory>;

export const SensitiveCategoriesFiltersInput = mkInput({
  name: 'SensitiveCategoriesFiltersInput',
  comment: 'Filters for sensitive categories',
  fields: {
    text: {
      comment: 'Find sensitive categories by text',
      optional: true,
      type: 'string',
    },
    ids: {
      comment: 'Find sensitive categories by ids',
      type: 'id',
      modelName: 'sensitiveCategory',
      list: true,
      optional: true,
    },
  },
});

/** Override type */
export type SensitiveCategoriesFiltersInput = SchemaToType<
  typeof SensitiveCategoriesFiltersInput
>;

export const CreateSensitiveCategoryInput = mkInput({
  name: 'CreateSensitiveCategoryInput',
  comment: 'Input for creating a sensitive category',
  fields: {
    title: {
      comment: 'The title of the sensitive category',
      type: 'string',
    },
    color: {
      comment: 'The color of the sensitive category',
      type: 'string',
    },
    description: {
      comment: 'The description of the sensitive category',
      type: 'string',
    },
  },
});

/** Override type */
export type CreateSensitiveCategoryInput = SchemaToType<
  typeof CreateSensitiveCategoryInput
>;

export const DeleteSensitiveCategoriesInput = mkInput({
  name: 'DeleteSensitiveCategoriesInput',
  comment: 'Input for deleting sensitive categories',
  fields: {
    ids: {
      comment: 'The list of IDs to delete',
      type: 'id',
      modelName: 'sensitiveCategory',
      list: true,
    },
  },
});

/** Override type */
export type DeleteSensitiveCategoriesInput = SchemaToType<
  typeof DeleteSensitiveCategoriesInput
>;

export const UpdateSensitiveCategoryInput = mkInput({
  name: 'UpdateSensitiveCategoryInput',
  comment: 'Input for updating a sensitive category',
  fields: {
    id: {
      comment: 'The sensitive category ID to update',
      type: 'id',
      modelName: 'sensitiveCategory',
    },
    title: {
      ...CreateSensitiveCategoryInput.fields.title,
      optional: true,
    },
    description: {
      ...CreateSensitiveCategoryInput.fields.description,
      optional: true,
    },
    color: {
      ...CreateSensitiveCategoryInput.fields.color,
      optional: true,
    },
  },
});

/** Override type */
export type UpdateSensitiveCategoryInput = SchemaToType<
  typeof UpdateSensitiveCategoryInput
>;

export const UpdateSensitiveCategoriesInput = mkInput({
  name: 'UpdateSensitiveCategoriesInput',
  comment: 'Input for bulk updating one or more sensitive categories',
  fields: {
    sensitiveCategories: {
      comment:
        'List of sensitive categories with the properties that should be updated for each',
      type: UpdateSensitiveCategoryInput,
      list: true,
    },
  },
});

/** Override type */
export type UpdateSensitiveCategoriesInput = SchemaToType<
  typeof UpdateSensitiveCategoriesInput
>;
